// src/pages/shop/ShopPurchaseReceive.jsx
//
// ПРИЁМКА накладной, прилетевшей от поставщика (фаза 2).
//
// Ключевые принципы:
//   • Вход ТОЛЬКО через саму накладную — она и есть эталон для сверки.
//   • Сканируешь штрихкод → находим ожидаемую строку → плюсуем ФАКТ.
//     Названия не важны: сверка идёт по штрихкоду.
//   • В остатки идёт ФАКТ, а не то, что заявил поставщик.
//   • Незнакомый штрихкод → предложение создать товар (название подскажет накладная).
//   • Строка без сопоставленного товара не даст принять — сначала свяжи или заведи.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { purchases as purchasesApi, products as productsApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency } from '../../hooks/useMyShop';
import { formatMoney } from '../../data/currencies';
import { errorMessage } from '../../utils/errorMessage';
import Icon from '../../components/Icon';
import SafeInput from '../../components/SafeInput';
import ScanButton from '../../components/ScanButton';

export default function ShopPurchaseReceive() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { show, ToastView } = useToast();
  const currency = useMyCurrency();
  const money = (v) => formatMoney(v, currency);

  const [inv, setInv] = useState(null);
  const [rows, setRows] = useState([]);          // строки приёмки
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  // Товар, которого нет в каталоге: заводим ПРЯМО ЗДЕСЬ.
  // Уводить в каталог нельзя — потеряется всё, что уже приняли.
  const [creating, setCreating] = useState(null);   // индекс строки

  useEffect(() => {
    (async () => {
      try {
        const data = await purchasesApi.get(id);
        if (data.status !== 'pending') {
          // Уже принята — смотреть надо в деталях, а не принимать заново
          navigate(`/purchase/${id}`, { replace: true });
          return;
        }
        setInv(data);
        setNote(data.note || '');
        setRows((data.items || []).map(i => ({
          id: i.id,
          name: i.name,
          variantName: i.variantName,
          unit: i.unit,
          barcode: i.barcode || null,
          expected: i.qtyExpected ?? 0,
          qty: 0,                                   // факт — заполняет приёмщик
          costPrice: i.costPrice,
          productId: i.productId || null,           // может быть null: товара нет в каталоге
          variantId: i.variantId || null,
          isExtra: i.qtyExpected == null,           // строка не из накладной поставщика
        })));
      } catch (e) {
        show(errorMessage(e, t), 'error');
        navigate('/purchases', { replace: true });
      }
      setLoading(false);
    })();
  }, [id]); // eslint-disable-line

  // ─── Сканирование: сверяем со строками накладной по штрихкоду ──
  const handleScan = async (code) => {
    const c = String(code || '').trim();
    if (!c) return;

    // 1) Есть ли такая строка в накладной?
    const idx = rows.findIndex(r => r.barcode && r.barcode === c);
    if (idx >= 0) {
      setRows(prev => prev.map((r, i) => i === idx ? { ...r, qty: (Number(r.qty) || 0) + 1 } : r));
      // Если товар ещё не сопоставлен с нашим каталогом — пробуем найти по штрихкоду
      if (!rows[idx].productId) await tryMatchProduct(idx, c);
      return;
    }

    // 2) Штрихкода нет в накладной — значит привезли лишнее (пересорт/довоз).
    //    Ищем товар у себя.
    try {
      const r = await productsApi.byBarcode(c);
      if (r?.product) {
        addExtraRow(r.product, r.variant, c);
        show(t('receive.extraAdded', { name: r.product.name }));
        return;
      }
    } catch (e) { /* нет такого товара */ }

    show(t('receive.unknownBarcode'), 'error');
  };

  // Пытаемся привязать строку накладной к товару в НАШЕМ каталоге
  const tryMatchProduct = async (idx, barcode) => {
    try {
      const r = await productsApi.byBarcode(barcode);
      if (r?.product) {
        setRows(prev => prev.map((row, i) => i === idx
          ? { ...row, productId: r.product.id, variantId: r.variant?.id || null }
          : row));
      }
    } catch (e) { /* товара нет — придётся создать */ }
  };

  const addExtraRow = (product, variant, barcode) => {
    const key = `extra:${variant?.id || product.id}`;
    setRows(prev => {
      const found = prev.find(r => r.key === key);
      if (found) return prev.map(r => r.key === key ? { ...r, qty: r.qty + 1 } : r);
      return [...prev, {
        key,
        id: null,                     // новой строки в накладной поставщика не было
        name: product.name,
        variantName: variant?.name || null,
        unit: product.unit || t('common.pieces'),
        barcode,
        expected: null,
        qty: 1,
        costPrice: Number(variant?.costPrice ?? product.costPrice ?? 0),
        productId: product.id,
        variantId: variant?.id || null,
        isExtra: true,
      }];
    });
  };

  // Заводим товар и сразу привязываем к строке приёмки
  const createProduct = async (idx, form) => {
    try {
      const created = await productsApi.create({
        name: form.name.trim(),
        barcode: form.barcode || undefined,
        unit: form.unit || undefined,
        costPrice: Number(form.costPrice) || 0,
        salePrice: Number(form.salePrice) || 0,
        stock: 0,                     // остаток добавится приёмкой
      });
      const variantId = created.variants?.[0]?.id || null;
      setRows(prev => prev.map((r, k) => k === idx
        ? { ...r, productId: created.id, variantId }
        : r));
      setCreating(null);
      show(t('receive.productCreated', { name: created.name }));
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
  };

  const setQty  = (i, v) => setRows(p => p.map((r, k) => k === i ? { ...r, qty: Math.max(0, Number(v) || 0) } : r));
  const setCost = (i, v) => setRows(p => p.map((r, k) => k === i ? { ...r, costPrice: Math.max(0, Number(v) || 0) } : r));
  const acceptAll = (i) => setRows(p => p.map((r, k) => k === i ? { ...r, qty: r.expected || 0 } : r));

  // Принять всё как заявлено — быстрый путь, когда расхождений нет
  const acceptEverything = () => setRows(p => p.map(r => r.expected != null ? { ...r, qty: r.expected } : r));

  const total = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.costPrice) || 0), 0),
    [rows],
  );

  // Строки, где принято, но товар не сопоставлен — их надо завести в каталоге
  const unmatched = rows.filter(r => r.qty > 0 && !r.productId && !r.variantId);

  const save = async () => {
    const taken = rows.filter(r => r.qty > 0);
    if (taken.length === 0) return show(t('receive.errNothing'), 'error');
    if (unmatched.length > 0) return show(t('receive.errUnmatched', { count: unmatched.length }), 'error');

    setSaving(true);
    try {
      await purchasesApi.accept(id, {
        note: note.trim() || undefined,
        items: taken.map(r => ({
          id: r.id || undefined,
          productId: r.productId,
          variantId: r.variantId,
          qty: r.qty,
          costPrice: r.costPrice,
        })),
      });
      show(t('receive.accepted'));
      navigate(`/purchase/${id}`, { replace: true });
    } catch (e) {
      show(errorMessage(e, t), 'error');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="page"><p style={{ padding: 24, color: 'var(--fg-muted)' }}>{t('common.loading')}</p></div>;
  }
  if (!inv) return null;

  return (
    <div className="page" style={{ paddingBottom: 150 }}>
      <div className="page-header">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', padding: 8, marginLeft: -8, cursor: 'pointer', color: 'var(--fg-muted)' }}
          aria-label={t('common.back')}
        >
          <Icon name="chevronLeft" size={22} stroke={2.5} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{inv.counterparty?.name}</p>
          <h1 className="page-title">{t('receive.title', { number: inv.number })}</h1>
        </div>
      </div>

      {/* Что заявил поставщик */}
      <div className="card" style={{ marginBottom: 12, background: 'var(--bg-subtle)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{t('receive.declared')}</span>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{money(inv.totalExpected || 0)}</span>
        </div>
        <p style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.4 }}>
          {t('receive.hint')}
        </p>
      </div>

      {/* Сканер */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          className="btn btn-secondary"
          onClick={acceptEverything}
          style={{ flex: 1 }}
        >
          {t('receive.acceptAllDeclared')}
        </button>
        <ScanButton onScan={handleScan} size="lg" />
      </div>

      {/* Строки */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, i) => {
          const diff = r.expected != null ? (Number(r.qty) || 0) - r.expected : null;
          const needsProduct = r.qty > 0 && !r.productId && !r.variantId;
          return (
            <div key={r.id || r.key || i} className="card" style={{
              padding: 12,
              border: needsProduct ? '1px solid var(--danger)' : undefined,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {r.name}
                    {r.variantName && <span style={{ color: 'var(--fg-muted)', fontWeight: 500 }}> · {r.variantName}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                    {r.expected != null
                      ? `${t('receive.expectedShort')}: ${r.expected} ${r.unit}`
                      : t('receive.extraLine')}
                    {diff != null && diff !== 0 && (
                      <span style={{ color: diff < 0 ? 'var(--danger)' : 'var(--warning, #F59E0B)', fontWeight: 700 }}>
                        {' · '}{diff > 0 ? '+' : ''}{diff}
                      </span>
                    )}
                  </div>
                </div>
                {r.expected != null && (
                  <button
                    onClick={() => acceptAll(i)}
                    style={{
                      background: 'none', border: '1px solid var(--border)',
                      borderRadius: 'var(--r-sm)', padding: '4px 8px',
                      fontSize: 11, cursor: 'pointer', color: 'var(--fg-muted)', whiteSpace: 'nowrap',
                    }}
                  >
                    {t('receive.takeAll')}
                  </button>
                )}
              </div>

              {needsProduct && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', marginBottom: 8,
                  background: 'var(--danger-soft, #FEE2E2)', borderRadius: 'var(--r-sm)',
                }}>
                  <Icon name="alert" size={14} color="var(--danger)" />
                  <span style={{ flex: 1, fontSize: 11, lineHeight: 1.3 }}>{t('receive.noProduct')}</span>
                  <button
                    onClick={() => setCreating(i)}
                    style={{
                      background: 'var(--danger)', border: 'none', color: '#fff',
                      borderRadius: 'var(--r-sm)', padding: '5px 9px',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {t('receive.createProduct')}
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <Field label={t('purchase.qtyFact')}>
                  <SafeInput
                    className="input" type="number" inputMode="decimal"
                    value={r.qty}
                    onChange={e => setQty(i, e.target.value)}
                    style={{ padding: '8px 10px', fontSize: 14 }}
                  />
                </Field>
                <Field label={t('purchase.costPrice')}>
                  <SafeInput
                    className="input" type="number" inputMode="decimal"
                    value={r.costPrice}
                    onChange={e => setCost(i, e.target.value)}
                    style={{ padding: '8px 10px', fontSize: 14 }}
                  />
                </Field>
                <div style={{ width: 88 }}>
                  <label style={{ fontSize: 10, color: 'var(--fg-muted)', display: 'block', marginBottom: 3 }}>
                    {t('purchase.sum')}
                  </label>
                  <div style={{
                    padding: '8px 10px', fontSize: 14, fontWeight: 700,
                    background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)',
                    textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden',
                  }}>
                    {money((Number(r.qty) || 0) * (Number(r.costPrice) || 0))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="input-group" style={{ marginTop: 12 }}>
        <label>{t('purchase.note')}</label>
        <SafeInput
          className="input"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder={t('purchase.notePh')}
        />
      </div>

      {/* Итог */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
        background: 'var(--bg)', borderTop: '1px solid var(--border)', zIndex: 50,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('receive.declared')}</span>
          <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{money(inv.totalExpected || 0)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{t('receive.actual')}</span>
          <span style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-display)' }}>{money(total)}</span>
        </div>
        <button className="btn btn-primary btn-lg" onClick={save} disabled={saving} style={{ width: '100%' }}>
          {saving ? t('common.saving') : t('receive.acceptBtn')}
        </button>
      </div>

      {creating !== null && (
        <QuickCreateSheet
          row={rows[creating]}
          onCreate={(form) => createProduct(creating, form)}
          onClose={() => setCreating(null)}
        />
      )}

      {ToastView}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Быстрое создание товара — не уходя с приёмки.
// Данные подставляются из накладной поставщика: название, штрихкод,
// закупочная цена. Остальное владелец допишет позже в каталоге.
// ═══════════════════════════════════════════════════════════════
function QuickCreateSheet({ row, onCreate, onClose }) {
  const { t } = useTranslation();

  const [name, setName] = useState(row?.name || '');
  const [barcode, setBarcode] = useState(row?.barcode || '');
  const [unit, setUnit] = useState(row?.unit || '');
  const [costPrice, setCostPrice] = useState(row?.costPrice ?? 0);
  const [salePrice, setSalePrice] = useState(row?.costPrice ?? 0);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    await onCreate({ name, barcode, unit, costPrice, salePrice });
    setBusy(false);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 200, display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', background: 'var(--bg)',
          borderRadius: '16px 16px 0 0', padding: 20,
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
          maxHeight: '85vh', overflowY: 'auto',
        }}
      >
        <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 4, fontFamily: 'var(--font-display)' }}>
          {t('receive.newProduct')}
        </h3>
        <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 16, lineHeight: 1.4 }}>
          {t('receive.newProductHint')}
        </p>

        <div className="input-group">
          <label>{t('products.name')}</label>
          <SafeInput className="input" value={name} onChange={e => setName(e.target.value)} />
        </div>

        <div className="input-group">
          <label>{t('products.barcode')}</label>
          <SafeInput className="input" value={barcode} onChange={e => setBarcode(e.target.value)} inputMode="numeric" />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="input-group" style={{ flex: 1 }}>
            <label>{t('products.costPrice')}</label>
            <SafeInput className="input" type="number" inputMode="decimal"
              value={costPrice} onChange={e => setCostPrice(e.target.value)} />
          </div>
          <div className="input-group" style={{ flex: 1 }}>
            <label>{t('products.salePrice')}</label>
            <SafeInput className="input" type="number" inputMode="decimal"
              value={salePrice} onChange={e => setSalePrice(e.target.value)} />
          </div>
        </div>

        <div className="input-group">
          <label>{t('products.unit')}</label>
          <SafeInput className="input" value={unit} onChange={e => setUnit(e.target.value)} />
        </div>

        <button
          className="btn btn-primary btn-lg"
          onClick={submit}
          disabled={busy || !name.trim()}
          style={{ width: '100%' }}
        >
          {busy ? t('common.saving') : t('common.create')}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ flex: 1 }}>
      <label style={{ fontSize: 10, color: 'var(--fg-muted)', display: 'block', marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}
